/**
 * Mock data for Business Categories (Industry Classifications)
 * Used for categorizing organizations by their business type/industry
 *
 * This determines which industry-specific documents are required
 *
 * @mock This data will be replaced with API calls
 */

export const mockBusinessCategories = [
  {
    id: "biz-cat-001",
    code: "GENERAL_TRADING",
    name: "General Trading & Services",
    nameAr: "التجارة العامة والخدمات",
    description: "General trading, import/export, and business services",
    icon: "store",
    color: "#607D8B",
    displayOrder: 1,
    isActive: true,
    isSystem: true,
    parentCategoryId: null,
    documentCount: 8,
    organizationCount: 120,
    licenseActivityCodes: ["47.11", "47.19", "46.90"],
    requiredDocuments: [], // Uses base rules only
    createdAt: "2024-01-01T00:00:00Z",
    _isMock: true
  },
  {
    id: "biz-cat-002",
    code: "FOOD_BEVERAGES",
    name: "Food & Beverages",
    nameAr: "الأغذية والمشروبات",
    description: "Restaurants, cafes, catering, food trading, and hospitality",
    icon: "restaurant",
    color: "#FF5722",
    displayOrder: 2,
    isActive: true,
    isSystem: true,
    parentCategoryId: null,
    documentCount: 12,
    organizationCount: 85,
    licenseActivityCodes: ["56.10", "56.21", "56.29", "56.30", "47.11"],
    requiredDocuments: [
      { documentTypeCode: "FOOD_LICENSE", isMandatory: true },
      { documentTypeCode: "MUNICIPALITY_FOOD_PERMIT", isMandatory: true },
      { documentTypeCode: "CIVIL_DEFENSE_CERT", isMandatory: true },
      { documentTypeCode: "FOOD_HANDLER_CARD", isMandatory: false },
      { documentTypeCode: "HEALTH_SAFETY_CERT", isMandatory: false }
    ],
    createdAt: "2024-01-01T00:00:00Z",
    _isMock: true
  },
  {
    id: "biz-cat-003",
    code: "HEALTHCARE",
    name: "Healthcare & Medical",
    nameAr: "الرعاية الصحية والطبية",
    description: "Clinics, hospitals, pharmacies, medical services, and healthcare facilities",
    icon: "local_hospital",
    color: "#E91E63",
    displayOrder: 3,
    isActive: true,
    isSystem: true,
    parentCategoryId: null,
    documentCount: 10,
    organizationCount: 45,
    licenseActivityCodes: ["86.10", "86.21", "86.22", "86.90", "47.73"],
    requiredDocuments: [
      { documentTypeCode: "DHA_LICENSE", isMandatory: true },
      { documentTypeCode: "MEDICAL_FACILITY_LICENSE", isMandatory: true },
      { documentTypeCode: "HEALTHCARE_PROFESSIONAL_LICENSE", isMandatory: false },
      { documentTypeCode: "PHARMACY_LICENSE", isMandatory: false, conditions: { hasPharmacy: true } }
    ],
    createdAt: "2024-01-01T00:00:00Z",
    _isMock: true
  },
  {
    id: "biz-cat-004",
    code: "REAL_ESTATE",
    name: "Real Estate & Property",
    nameAr: "العقارات",
    description: "Real estate brokerage, property management, and development",
    icon: "apartment",
    color: "#9C27B0",
    displayOrder: 4,
    isActive: true,
    isSystem: true,
    parentCategoryId: null,
    documentCount: 9,
    organizationCount: 65,
    licenseActivityCodes: ["68.10", "68.20", "68.31", "68.32"],
    requiredDocuments: [
      { documentTypeCode: "RERA_LICENSE", isMandatory: true },
      { documentTypeCode: "ESCROW_CERTIFICATE", isMandatory: false, conditions: { isDeveloper: true } },
      { documentTypeCode: "BROKER_CARD", isMandatory: false }
    ],
    createdAt: "2024-01-01T00:00:00Z",
    _isMock: true
  },
  {
    id: "biz-cat-005",
    code: "CONSTRUCTION",
    name: "Construction & Contracting",
    nameAr: "البناء والمقاولات",
    description: "Construction, contracting, engineering, and building services",
    icon: "construction",
    color: "#795548",
    displayOrder: 5,
    isActive: true,
    isSystem: true,
    parentCategoryId: null,
    documentCount: 11,
    organizationCount: 55,
    licenseActivityCodes: ["41.10", "41.20", "42.10", "42.21", "43.11"],
    requiredDocuments: [
      { documentTypeCode: "CIVIL_DEFENSE_CERT", isMandatory: true },
      { documentTypeCode: "SAFETY_PERMIT", isMandatory: true },
      { documentTypeCode: "CONTRACTOR_LICENSE", isMandatory: true },
      { documentTypeCode: "ENGINEERING_CONSULTANCY_LICENSE", isMandatory: false }
    ],
    createdAt: "2024-01-01T00:00:00Z",
    _isMock: true
  },
  {
    id: "biz-cat-006",
    code: "EDUCATION",
    name: "Education & Training",
    nameAr: "التعليم والتدريب",
    description: "Schools, training centers, educational institutions, and e-learning",
    icon: "school",
    color: "#3F51B5",
    displayOrder: 6,
    isActive: true,
    isSystem: true,
    parentCategoryId: null,
    documentCount: 10,
    organizationCount: 35,
    licenseActivityCodes: ["85.10", "85.20", "85.31", "85.32", "85.41"],
    requiredDocuments: [
      { documentTypeCode: "KHDA_LICENSE", isMandatory: true },
      { documentTypeCode: "MOE_APPROVAL", isMandatory: false, conditions: { isSchool: true } },
      { documentTypeCode: "ACCREDITATION_CERT", isMandatory: false }
    ],
    createdAt: "2024-01-01T00:00:00Z",
    _isMock: true
  },
  {
    id: "biz-cat-007",
    code: "PROFESSIONAL_SERVICES",
    name: "Professional Services",
    nameAr: "الخدمات المهنية",
    description: "Consulting, legal, accounting, and professional advisory services",
    icon: "business_center",
    color: "#00BCD4",
    displayOrder: 7,
    isActive: true,
    isSystem: true,
    parentCategoryId: null,
    documentCount: 8,
    organizationCount: 90,
    licenseActivityCodes: ["69.10", "69.20", "70.10", "70.21", "70.22"],
    requiredDocuments: [
      { documentTypeCode: "PROFESSIONAL_LICENSE", isMandatory: true }
    ],
    createdAt: "2024-01-01T00:00:00Z",
    _isMock: true
  },
  {
    id: "biz-cat-008",
    code: "TECHNOLOGY",
    name: "IT & Technology",
    nameAr: "تكنولوجيا المعلومات",
    description: "Software development, IT services, and technology solutions",
    icon: "computer",
    color: "#2196F3",
    displayOrder: 8,
    isActive: true,
    isSystem: true,
    parentCategoryId: null,
    documentCount: 8,
    organizationCount: 75,
    licenseActivityCodes: ["62.01", "62.02", "62.03", "62.09", "63.11"],
    requiredDocuments: [], // Uses base rules only
    createdAt: "2024-01-01T00:00:00Z",
    _isMock: true
  },
  {
    id: "biz-cat-009",
    code: "RETAIL",
    name: "Retail & E-commerce",
    nameAr: "التجزئة والتجارة الإلكترونية",
    description: "Retail stores, e-commerce, and consumer goods",
    icon: "shopping_cart",
    color: "#4CAF50",
    displayOrder: 9,
    isActive: true,
    isSystem: true,
    parentCategoryId: null,
    documentCount: 8,
    organizationCount: 110,
    licenseActivityCodes: ["47.11", "47.19", "47.41", "47.91"],
    requiredDocuments: [
      { documentTypeCode: "ECOMMERCE_LICENSE", isMandatory: false, conditions: { hasOnlineStore: true } }
    ],
    createdAt: "2024-01-01T00:00:00Z",
    _isMock: true
  },
  {
    id: "biz-cat-010",
    code: "MANUFACTURING",
    name: "Manufacturing & Industrial",
    nameAr: "التصنيع والصناعة",
    description: "Manufacturing, production, and industrial operations",
    icon: "factory",
    color: "#FF9800",
    displayOrder: 10,
    isActive: true,
    isSystem: true,
    parentCategoryId: null,
    documentCount: 9,
    organizationCount: 25,
    licenseActivityCodes: ["10.11", "25.11", "28.11", "29.10"],
    requiredDocuments: [
      { documentTypeCode: "INDUSTRIAL_LICENSE", isMandatory: true },
      { documentTypeCode: "ENVIRONMENTAL_PERMIT", isMandatory: true },
      { documentTypeCode: "CIVIL_DEFENSE_CERT", isMandatory: true }
    ],
    createdAt: "2024-01-01T00:00:00Z",
    _isMock: true
  },
  {
    id: "biz-cat-011",
    code: "LOGISTICS",
    name: "Logistics & Transport",
    nameAr: "الخدمات اللوجستية والنقل",
    description: "Freight, shipping, warehousing, and transportation services",
    icon: "local_shipping",
    color: "#673AB7",
    displayOrder: 11,
    isActive: true,
    isSystem: true,
    parentCategoryId: null,
    documentCount: 9,
    organizationCount: 40,
    licenseActivityCodes: ["49.41", "49.42", "52.10", "52.21", "52.29"],
    requiredDocuments: [
      { documentTypeCode: "TRANSPORT_LICENSE", isMandatory: true },
      { documentTypeCode: "CUSTOMS_BROKER_LICENSE", isMandatory: false }
    ],
    createdAt: "2024-01-01T00:00:00Z",
    _isMock: true
  },
  {
    id: "biz-cat-012",
    code: "TOURISM",
    name: "Tourism & Hospitality",
    nameAr: "السياحة والضيافة",
    description: "Hotels, travel agencies, tourism, and hospitality services",
    icon: "flight",
    color: "#009688",
    displayOrder: 12,
    isActive: true,
    isSystem: true,
    parentCategoryId: null,
    documentCount: 10,
    organizationCount: 50,
    licenseActivityCodes: ["55.10", "55.20", "79.11", "79.12", "79.90"],
    requiredDocuments: [
      { documentTypeCode: "TOURISM_LICENSE", isMandatory: true },
      { documentTypeCode: "DTCM_PERMIT", isMandatory: true },
      { documentTypeCode: "HOTEL_LICENSE", isMandatory: false, conditions: { isHotel: true } }
    ],
    createdAt: "2024-01-01T00:00:00Z",
    _isMock: true
  }
];

/**
 * Industry-specific document types
 * These are additional document types required based on business category
 */
export const industrySpecificDocumentTypes = [
  // Food & Beverages
  {
    id: "type-food-001",
    code: "FOOD_LICENSE",
    name: "Food License",
    nameAr: "رخصة الغذاء",
    categoryId: "cat-license-001",
    verticalId: "vert-uae-001",
    businessCategoryCode: "FOOD_BEVERAGES",
    hasExpiry: true,
    expiryReminderDays: [90, 60, 30, 7],
    authorityCode: "MUNICIPALITY",
    authorityName: "Dubai Municipality",
    isActive: true,
    _isMock: true
  },
  {
    id: "type-food-002",
    code: "MUNICIPALITY_FOOD_PERMIT",
    name: "Municipality Food Permit",
    nameAr: "تصريح البلدية للأغذية",
    categoryId: "cat-license-001",
    verticalId: "vert-uae-001",
    businessCategoryCode: "FOOD_BEVERAGES",
    hasExpiry: true,
    expiryReminderDays: [90, 60, 30, 7],
    authorityCode: "MUNICIPALITY",
    authorityName: "Dubai Municipality",
    isActive: true,
    _isMock: true
  },
  {
    id: "type-food-003",
    code: "CIVIL_DEFENSE_CERT",
    name: "Civil Defense Certificate",
    nameAr: "شهادة الدفاع المدني",
    categoryId: "cat-license-001",
    verticalId: "vert-uae-001",
    businessCategoryCode: "FOOD_BEVERAGES,CONSTRUCTION,MANUFACTURING",
    hasExpiry: true,
    expiryReminderDays: [90, 60, 30, 7],
    authorityCode: "CIVIL_DEFENSE",
    authorityName: "Dubai Civil Defense",
    isActive: true,
    _isMock: true
  },
  {
    id: "type-food-004",
    code: "FOOD_HANDLER_CARD",
    name: "Food Handler Card",
    nameAr: "بطاقة متداول الأغذية",
    categoryId: "cat-govcard-002",
    verticalId: "vert-uae-001",
    businessCategoryCode: "FOOD_BEVERAGES",
    hasExpiry: true,
    expiryReminderDays: [60, 30, 7],
    authorityCode: "MUNICIPALITY",
    authorityName: "Dubai Municipality",
    isActive: true,
    _isMock: true
  },
  // Healthcare
  {
    id: "type-health-001",
    code: "DHA_LICENSE",
    name: "DHA Medical License",
    nameAr: "رخصة هيئة الصحة بدبي",
    categoryId: "cat-license-001",
    verticalId: "vert-uae-001",
    businessCategoryCode: "HEALTHCARE",
    hasExpiry: true,
    expiryReminderDays: [90, 60, 30, 7],
    authorityCode: "DHA",
    authorityName: "Dubai Health Authority",
    isActive: true,
    _isMock: true
  },
  {
    id: "type-health-002",
    code: "MEDICAL_FACILITY_LICENSE",
    name: "Medical Facility License",
    nameAr: "رخصة المنشأة الطبية",
    categoryId: "cat-license-001",
    verticalId: "vert-uae-001",
    businessCategoryCode: "HEALTHCARE",
    hasExpiry: true,
    expiryReminderDays: [90, 60, 30, 7],
    authorityCode: "DHA",
    authorityName: "Dubai Health Authority",
    isActive: true,
    _isMock: true
  },
  // Real Estate
  {
    id: "type-realestate-001",
    code: "RERA_LICENSE",
    name: "RERA Broker License",
    nameAr: "رخصة الوسيط العقاري",
    categoryId: "cat-license-001",
    verticalId: "vert-uae-001",
    businessCategoryCode: "REAL_ESTATE",
    hasExpiry: true,
    expiryReminderDays: [90, 60, 30, 7],
    authorityCode: "RERA",
    authorityName: "Real Estate Regulatory Agency",
    isActive: true,
    _isMock: true
  },
  {
    id: "type-realestate-002",
    code: "ESCROW_CERTIFICATE",
    name: "Escrow Account Certificate",
    nameAr: "شهادة حساب الضمان",
    categoryId: "cat-license-001",
    verticalId: "vert-uae-001",
    businessCategoryCode: "REAL_ESTATE",
    hasExpiry: false,
    authorityCode: "RERA",
    authorityName: "Real Estate Regulatory Agency",
    isActive: true,
    _isMock: true
  },
  // Education
  {
    id: "type-edu-001",
    code: "KHDA_LICENSE",
    name: "KHDA Education License",
    nameAr: "رخصة هيئة المعرفة",
    categoryId: "cat-license-001",
    verticalId: "vert-uae-001",
    businessCategoryCode: "EDUCATION",
    hasExpiry: true,
    expiryReminderDays: [90, 60, 30, 7],
    authorityCode: "KHDA",
    authorityName: "Knowledge & Human Development Authority",
    isActive: true,
    _isMock: true
  },
  {
    id: "type-edu-002",
    code: "MOE_APPROVAL",
    name: "MOE School Approval",
    nameAr: "موافقة وزارة التربية",
    categoryId: "cat-license-001",
    verticalId: "vert-uae-001",
    businessCategoryCode: "EDUCATION",
    hasExpiry: true,
    expiryReminderDays: [90, 60, 30, 7],
    authorityCode: "MOE",
    authorityName: "Ministry of Education",
    isActive: true,
    _isMock: true
  },
  // Construction
  {
    id: "type-const-001",
    code: "CONTRACTOR_LICENSE",
    name: "Contractor License",
    nameAr: "رخصة المقاول",
    categoryId: "cat-license-001",
    verticalId: "vert-uae-001",
    businessCategoryCode: "CONSTRUCTION",
    hasExpiry: true,
    expiryReminderDays: [90, 60, 30, 7],
    authorityCode: "MUNICIPALITY",
    authorityName: "Dubai Municipality",
    isActive: true,
    _isMock: true
  },
  {
    id: "type-const-002",
    code: "SAFETY_PERMIT",
    name: "Safety Permit",
    nameAr: "تصريح السلامة",
    categoryId: "cat-license-001",
    verticalId: "vert-uae-001",
    businessCategoryCode: "CONSTRUCTION",
    hasExpiry: true,
    expiryReminderDays: [90, 60, 30, 7],
    authorityCode: "CIVIL_DEFENSE",
    authorityName: "Dubai Civil Defense",
    isActive: true,
    _isMock: true
  },
  // Manufacturing
  {
    id: "type-mfg-001",
    code: "INDUSTRIAL_LICENSE",
    name: "Industrial License",
    nameAr: "الرخصة الصناعية",
    categoryId: "cat-license-001",
    verticalId: "vert-uae-001",
    businessCategoryCode: "MANUFACTURING",
    hasExpiry: true,
    expiryReminderDays: [90, 60, 30, 7],
    authorityCode: "MOIAT",
    authorityName: "Ministry of Industry & Advanced Technology",
    isActive: true,
    _isMock: true
  },
  {
    id: "type-mfg-002",
    code: "ENVIRONMENTAL_PERMIT",
    name: "Environmental Permit",
    nameAr: "التصريح البيئي",
    categoryId: "cat-license-001",
    verticalId: "vert-uae-001",
    businessCategoryCode: "MANUFACTURING",
    hasExpiry: true,
    expiryReminderDays: [90, 60, 30, 7],
    authorityCode: "MOCCAE",
    authorityName: "Ministry of Climate Change & Environment",
    isActive: true,
    _isMock: true
  },
  // Logistics
  {
    id: "type-log-001",
    code: "TRANSPORT_LICENSE",
    name: "Transport License",
    nameAr: "رخصة النقل",
    categoryId: "cat-license-001",
    verticalId: "vert-uae-001",
    businessCategoryCode: "LOGISTICS",
    hasExpiry: true,
    expiryReminderDays: [90, 60, 30, 7],
    authorityCode: "RTA",
    authorityName: "Roads & Transport Authority",
    isActive: true,
    _isMock: true
  },
  // Tourism
  {
    id: "type-tour-001",
    code: "TOURISM_LICENSE",
    name: "Tourism License",
    nameAr: "رخصة السياحة",
    categoryId: "cat-license-001",
    verticalId: "vert-uae-001",
    businessCategoryCode: "TOURISM",
    hasExpiry: true,
    expiryReminderDays: [90, 60, 30, 7],
    authorityCode: "DTCM",
    authorityName: "Dept of Tourism & Commerce Marketing",
    isActive: true,
    _isMock: true
  },
  {
    id: "type-tour-002",
    code: "DTCM_PERMIT",
    name: "DTCM Permit",
    nameAr: "تصريح دائرة السياحة",
    categoryId: "cat-license-001",
    verticalId: "vert-uae-001",
    businessCategoryCode: "TOURISM",
    hasExpiry: true,
    expiryReminderDays: [90, 60, 30, 7],
    authorityCode: "DTCM",
    authorityName: "Dept of Tourism & Commerce Marketing",
    isActive: true,
    _isMock: true
  }
];

// Helper functions
export const getBusinessCategoryById = (id) => {
  return mockBusinessCategories.find(c => c.id === id);
};

export const getBusinessCategoryByCode = (code) => {
  return mockBusinessCategories.find(c => c.code === code);
};

export const getActiveBusinessCategories = () => {
  return mockBusinessCategories.filter(c => c.isActive);
};

export const getRequiredDocumentsForCategory = (categoryCode) => {
  const category = getBusinessCategoryByCode(categoryCode);
  if (!category) return [];
  return category.requiredDocuments || [];
};

export const getIndustrySpecificDocTypes = (businessCategoryCode) => {
  return industrySpecificDocumentTypes.filter(dt =>
    dt.businessCategoryCode.includes(businessCategoryCode)
  );
};

export default mockBusinessCategories;
